import { IconAlertCircle, IconCircleCheck, IconEye } from "@tabler/icons-react";
import { Tooltip } from "@mantine/core";
import CustomBadge from "components/ui/CustomBadge/CustomBadge";
import CustomTable from "components/ui/CustomTable";
import CustomText from "components/ui/CustomText/CustomText";
import GroupActions from "components/ui/GroupActions";
import { ActionItem } from "components/ui/GroupActions/GroupActions";
import { useAbility } from "configs/casl";
import { canAccessRequirement } from "utils/helpers/authorization";
import {
  EXPORT_JOB_STATUS_COLOR,
  EXPORT_JOB_STATUS_LABEL,
  EXPORT_MODULE_LABEL,
} from "constants/common/exportJob";
import { ExportJobResponseType } from "constants/types/export";
import { Loading } from "components";
import { MRT_ColumnDef } from "mantine-react-table";
import { FC, useMemo } from "react";
import { formatDate } from "utils/helpers/format";
import { useExportJobs } from "utils/hooks/request/useExportJobs";
import { useUsers } from "utils/hooks/request/useUsers";
import { useAppModal } from "utils/hooks/ui/useAppModal";
import ExportJobDetailModal from "../ExportJobDetailModal/ExportJobDetailModal";

const ICON_SIZE = 18;

const ExportJobsTable: FC = () => {
  const { jobs, isLoading } = useExportJobs();
  const { users } = useUsers({ limit: 100 });
  const { openCrudModal } = useAppModal();
  const ability = useAbility();

  const userMap = useMemo(() => {
    const map: Record<string, string> = {};
    users?.forEach((u) => {
      map[u.id] = u.fullname;
    });
    return map;
  }, [users]);

  const handleOpenDetailModal = (job: ExportJobResponseType) => {
    openCrudModal(
      <CustomText variant="section-title">Chi tiết xuất file</CustomText>,
      {
        mode: "view",
        children: <ExportJobDetailModal jobId={job.id} userMap={userMap} />,
      },
      560,
    );
  };

  const actions: ActionItem<ExportJobResponseType>[] = useMemo(
    () => [
      {
        key: "view",
        label: "Xem chi tiết",
        icon: <IconEye size={ICON_SIZE} />,
        color: "blue",
        permission: "export_job:read",
        onClick: handleOpenDetailModal,
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [userMap],
  );

  const hasActions = useMemo(
    () => actions.some((a) => canAccessRequirement(ability, a.permission, true)),
    [actions, ability],
  );

  const columns: MRT_ColumnDef<ExportJobResponseType>[] = useMemo(
    () => [
      {
        id: "stt",
        header: "STT",
        size: 50,
        grow: false,
        Cell: ({ row }) => row.index + 1,
        mantineTableHeadCellProps: { align: "center" },
        mantineTableBodyCellProps: { align: "center" },
      },
      {
        accessorKey: "module",
        header: "Module",
        Cell: ({ cell }) => {
          const val = cell.getValue<string>();
          return EXPORT_MODULE_LABEL[val] ?? val;
        },
      },
      {
        id: "dateRange",
        header: "Khoảng thời gian",
        Cell: ({ row }) => {
          const { fromDate, toDate } = row.original;
          if (!fromDate && !toDate) return "—";
          return `${fromDate ? formatDate(fromDate) : "—"} → ${toDate ? formatDate(toDate) : "—"}`;
        },
      },
      {
        accessorKey: "status",
        header: "Trạng thái",
        Cell: ({ cell }) => {
          const status = cell.getValue<string>();
          return (
            <CustomBadge
              label={EXPORT_JOB_STATUS_LABEL[status] ?? status}
              color={EXPORT_JOB_STATUS_COLOR[status] ?? "gray"}
            />
          );
        },
      },
      {
        accessorKey: "errorMessage",
        header: "Lỗi",
        size: 70,
        grow: false,
        mantineTableHeadCellProps: { align: "center" },
        mantineTableBodyCellProps: { align: "center" },
        Cell: ({ cell }) => {
          const val = cell.getValue<string | null>();
          return val ? (
            <Tooltip label={val} withArrow position="top" multiline maw={300}>
              <IconAlertCircle
                size={ICON_SIZE}
                color="red"
                style={{ display: "block" }}
              />
            </Tooltip>
          ) : (
            <IconCircleCheck
              size={ICON_SIZE}
              color="green"
              style={{ display: "block" }}
            />
          );
        },
      },
      {
        accessorKey: "requestedById",
        header: "Người xuất",
        Cell: ({ cell }) => {
          const id = cell.getValue<string | null>();
          return id ? (userMap[id] ?? id) : "—";
        },
      },
      {
        accessorKey: "createdAt",
        header: "Ngày tạo",
        Cell: ({ cell }) => {
          const val = cell.getValue<number>();
          return val ? formatDate(val) : "—";
        },
      },
      ...(hasActions
        ? [
            {
              id: "actions",
              header: "Thao tác",
              enableSorting: false,
              enableColumnFilter: false,
              size: 80,
              grow: false,
              enableResizing: false,
              mantineTableHeadCellProps: { align: "center" },
              mantineTableBodyCellProps: { align: "center" },
              Cell: ({ row }: any) => (
                <GroupActions row={row.original} actions={actions} />
              ),
            } satisfies MRT_ColumnDef<ExportJobResponseType>,
          ]
        : []),
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [actions, userMap, hasActions],
  );

  return (
    <Loading variant="overlay" visible={isLoading}>
      <CustomTable columns={columns} data={jobs} />
    </Loading>
  );
};

export default ExportJobsTable;
